import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { ObjectSchema } from 'yup';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { FormField } from '@components/forms/FormField';
import { PasswordStrengthMeter } from '@components/forms/PasswordStrengthMeter';
import { Button } from '@components/ui/Button';
import { useAuthOperations } from '@hooks/useApi';
import { ResetPasswordForm } from '@defs/forms';

// ============================================================================
// Validation Schema
// ============================================================================

const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .min(12, 'La contraseña debe tener al menos 12 caracteres')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/,
      'La contraseña debe incluir mayúsculas, minúsculas y números',
    )
    .test(
      'no-leading-trailing-space',
      'No uses espacios al inicio o al final',
      (value) => (value ? value.trim() === value : true),
    )
    .required('Contraseña es requerida'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden')
    .required('Confirmar contraseña es requerido'),
  token: yup.string().required('Token es requerido'),
}) as ObjectSchema<ResetPasswordForm>;

// ============================================================================
// Styled Components
// ============================================================================

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const HelperRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  font-size: 0.95rem;
  text-align: center;

  a {
    color: var(--brand-600);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--brand-600);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const ErrorMessage = styled.div`
  background: var(--danger-50);
  color: var(--danger-600);
  padding: 1rem;
  border-radius: var(--radius-md);
  border-left: 4px solid var(--danger-500);
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

// ============================================================================
// Component
// ============================================================================

export const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword, isLoading } = useAuthOperations();

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onBlur',
    defaultValues: {
      token: token || '',
    },
  });

  // Watch password to show requirements
  const password = watch('password');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenError(
        'Este enlace está incompleto. Solicita uno nuevo para restablecer tu contraseña.',
      );
      return;
    }

    // Token validation could be done here if needed
    setTokenValid(true);
  }, [token]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!tokenValid) {
      toast.error('El enlace no es válido o ha expirado');
      return;
    }

    try {
      await resetPassword({
        token: data.token,
        new_password: data.password,
      });

      toast.success(
        'Contraseña restablecida exitosamente. Ahora puedes iniciar sesión.',
        { duration: 6000 },
      );

      // Redirect to login page
      navigate('/auth/login', {
        state: {
          message:
            'Contraseña restablecida. Por favor inicia sesión con tu nueva contraseña.',
        },
      });
    } catch (error: unknown) {
      const apiError = error as {
        status?: number;
        detail?: string;
        message?: string;
      };

      if (apiError.status === 400) {
        toast.error(
          'El enlace no es válido o ha expirado. Solicita un nuevo enlace de restablecimiento.',
        );
        setTokenValid(false);
        setTokenError('El enlace no es válido o ha expirado');
      } else if (apiError.status === 422) {
        toast.error(
          'Datos inválidos. Verifica la información e intenta de nuevo.',
        );
      } else if (apiError.detail) {
        toast.error(apiError.detail);
      } else {
        toast.error('Error al restablecer la contraseña. Intenta de nuevo.');
      }
    }
  };

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <>
        <h1 className="auth-title">Restablecer contraseña</h1>
        <ErrorMessage role="alert">
          <strong>Error:</strong>{' '}
          {tokenError ||
            'El enlace de restablecimiento es inválido o ha expirado.'}
        </ErrorMessage>
        <p>
          Solicita un nuevo enlace de restablecimiento desde la página de inicio
          de sesión.
        </p>
        <HelperRow>
          <a href="/auth/login">Ir a inicio de sesión</a>
        </HelperRow>
      </>
    );
  }

  // Show loading while validating token
  if (tokenValid === null) {
    return (
      <>
        <h1 className="auth-title">Restablecer contraseña</h1>
        <p>Preparando formulario…</p>
      </>
    );
  }

  return (
    <>
      <h1 className="auth-title">Restablecer contraseña</h1>
      <p className="auth-subtitle">Ingresa tu nueva contraseña segura</p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField
          label="Nueva contraseña"
          name="password"
          type="password"
          placeholder="••••••••••••"
          required
          autoComplete="new-password"
          register={register}
          error={errors.password}
        />

        <PasswordStrengthMeter password={password} />

        <FormField
          label="Confirmar nueva contraseña"
          name="confirmPassword"
          type="password"
          placeholder="••••••••••••"
          required
          autoComplete="new-password"
          register={register}
          error={errors.confirmPassword}
        />

        {/* Hidden token field */}
        <input type="hidden" {...register('token')} />

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            Restablecer contraseña
          </Button>
        </FormActions>
      </form>

      <HelperRow>
        <span>
          ¿Recordaste tu contraseña? <a href="/auth/login">Iniciar sesión</a>
        </span>
      </HelperRow>
    </>
  );
};
